/**
 * StreamPreview - Camera/microphone preview component with device selection
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';

interface StreamPreviewProps {
  onStreamReady: (stream: MediaStream) => void;
  onStreamError: (error: Error) => void;
  isLive: boolean;
}

interface DeviceInfo {
  deviceId: string;
  label: string;
}

export const StreamPreview: React.FC<StreamPreviewProps> = ({
  onStreamReady,
  onStreamError,
  isLive,
}) => {
  // Render logging removed to avoid console spam (was causing ~75 logs/second with audio level updates)

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>(0);
  const audioLevelBarRef = useRef<HTMLDivElement>(null); // Direct DOM ref for audio level (avoids re-renders)

  // Track whether component is truly mounted (to handle React.StrictMode double-invoke)
  const isMountedRef = useRef<boolean>(true);
  // Store stream in ref to avoid closure issues in cleanup
  const streamRef = useRef<MediaStream | null>(null);
  // Track if we've already passed a stream to parent (to avoid stopping it during StrictMode remount)
  const streamPassedToParentRef = useRef<boolean>(false);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [videoDevices, setVideoDevices] = useState<DeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<DeviceInfo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string>('');
  const [selectedAudio, setSelectedAudio] = useState<string>('');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  // audioLevel state removed - using direct DOM manipulation via audioLevelBarRef instead
  const [error, setError] = useState<string | null>(null);

  // Enumerate devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        // Request permission first to get device labels
        const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });

        // Stop the permission stream immediately - we just needed it for permissions
        permissionStream.getTracks().forEach((track) => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();

        const videos = devices
          .filter((d) => d.kind === 'videoinput')
          .map((d) => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 8)}` }));

        const audios = devices
          .filter((d) => d.kind === 'audioinput')
          .map((d) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 8)}` }));

        setVideoDevices(videos);
        setAudioDevices(audios);

        if (videos.length > 0 && !selectedVideo) {
          setSelectedVideo(videos[0].deviceId);
        }
        if (audios.length > 0 && !selectedAudio) {
          setSelectedAudio(audios[0].deviceId);
        }
      } catch (err) {
        console.error('Failed to enumerate devices:', err);
        setError('Could not access camera/microphone. Please check permissions.');
        onStreamError(err as Error);
      }
    };

    getDevices();
  }, []);

  // Start media stream
  const startStream = useCallback(async () => {
    console.log('[StreamPreview] Starting stream with devices:', {
      selectedVideo,
      selectedAudio,
      isMounted: isMountedRef.current,
      existingStreamRef: !!streamRef.current,
      streamPassedToParent: streamPassedToParentRef.current,
    });

    // Don't start if component is unmounted
    if (!isMountedRef.current) {
      console.log('[StreamPreview] Component not mounted, skipping startStream');
      return;
    }

    try {
      // Stop existing stream from ref (more reliable than state)
      if (streamRef.current) {
        console.log('[StreamPreview] Stopping existing stream from ref:', streamRef.current.id);
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      const constraints: MediaStreamConstraints = {
        video: selectedVideo
          ? { deviceId: { exact: selectedVideo }, width: 1920, height: 1080, frameRate: 30 }
          : { width: 1920, height: 1080, frameRate: 30 },
        audio: selectedAudio
          ? { deviceId: { exact: selectedAudio }, echoCancellation: true, noiseSuppression: true }
          : { echoCancellation: true, noiseSuppression: true },
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Check if still mounted after async operation
      if (!isMountedRef.current) {
        console.log('[StreamPreview] Component unmounted during getUserMedia, stopping new stream');
        newStream.getTracks().forEach((track) => track.stop());
        return;
      }

      // Store in ref AND state
      streamRef.current = newStream;
      setStream(newStream);
      setError(null);

      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }

      // Set up audio analyzer
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const source = audioContextRef.current.createMediaStreamSource(newStream);
      source.connect(analyser);

      console.log('[StreamPreview] Media stream ready, calling onStreamReady', {
        videoTracks: newStream.getVideoTracks().length,
        audioTracks: newStream.getAudioTracks().length,
        streamId: newStream.id,
        streamActive: newStream.active,
      });

      // Mark that we've passed a stream to the parent
      streamPassedToParentRef.current = true;
      onStreamReady(newStream);
      console.log('[StreamPreview] onStreamReady callback completed, stream passed to parent');
    } catch (err) {
      console.error('Failed to start stream:', err);
      setError('Could not start camera/microphone. Please check permissions.');
      onStreamError(err as Error);
    }
  }, [selectedVideo, selectedAudio, onStreamReady, onStreamError]);

  // Track mounted state for React.StrictMode handling
  useEffect(() => {
    console.log('[StreamPreview] Mount effect running, setting isMounted = true');
    isMountedRef.current = true;

    return () => {
      console.log('[StreamPreview] Unmount cleanup, setting isMounted = false');
      isMountedRef.current = false;
      // Only clean up streams on true unmount - DON'T stop the stream here
      // because it might be in use by the parent component (GoLive)
      // The parent is responsible for the stream lifecycle once we pass it via onStreamReady
    };
  }, []);

  // Start stream when devices are selected
  useEffect(() => {
    console.log('[StreamPreview] Device selection effect triggered', {
      selectedVideo,
      selectedAudio,
      isMounted: isMountedRef.current,
      streamPassedToParent: streamPassedToParentRef.current,
    });

    if (selectedVideo && selectedAudio) {
      console.log('[StreamPreview] Both devices selected, calling startStream');
      startStream();
    } else {
      console.log('[StreamPreview] Devices not yet selected, skipping startStream');
    }

    // Cleanup function - be careful with React.StrictMode
    return () => {
      console.log('[StreamPreview] Device selection cleanup running', {
        hasStreamRef: !!streamRef.current,
        streamPassedToParent: streamPassedToParentRef.current,
        isMounted: isMountedRef.current,
      });

      // If stream was passed to parent, DON'T stop it here
      // The parent component (GoLive) now owns the stream
      if (streamPassedToParentRef.current) {
        console.log('[StreamPreview] Stream was passed to parent, NOT stopping in cleanup');
        // Clear our local reference but don't stop the tracks
        streamRef.current = null;
      } else if (streamRef.current) {
        // Only stop stream if it was never passed to parent
        console.log('[StreamPreview] Stream NOT passed to parent, stopping tracks');
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Clean up audio context
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      cancelAnimationFrame(animationFrameRef.current);
    };
    // Note: startStream is intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideo, selectedAudio]);

  // Audio level visualization - Uses direct DOM manipulation to avoid ~75 re-renders/second
  useEffect(() => {
    if (!analyserRef.current) return;

    const updateAudioLevel = () => {
      if (!analyserRef.current) return;

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);

      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      const normalizedLevel = Math.min(100, (average / 128) * 100);

      // Direct DOM manipulation instead of setState to avoid re-renders
      if (audioLevelBarRef.current) {
        audioLevelBarRef.current.style.width = `${normalizedLevel}%`;
        audioLevelBarRef.current.style.background = normalizedLevel > 80 ? '#ef4444' : '#22c55e';
      }

      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    };

    updateAudioLevel();

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [stream]);

  // Toggle mute
  const toggleMute = () => {
    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (stream) {
      stream.getVideoTracks().forEach((track) => {
        track.enabled = isVideoOff;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  return (
    <div className="stream-preview">
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
          <i className="fas fa-video" style={{ marginRight: '8px', color: 'var(--primary)' }} />
          Preview
        </h3>
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)' }}>
          Check your camera and microphone before going live
        </p>
      </div>

      {/* Video Preview */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16/9',
          background: '#000',
          borderRadius: '12px',
          overflow: 'hidden',
          marginBottom: '16px',
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: 'scaleX(-1)', // Mirror effect
            display: isVideoOff ? 'none' : 'block',
          }}
        />

        {/* Video Off Overlay */}
        {isVideoOff && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-secondary)',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <i className="fas fa-video-slash" style={{ fontSize: '48px', color: 'var(--text-secondary)', marginBottom: '8px' }} />
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Camera Off</p>
            </div>
          </div>
        )}

        {/* Live Indicator */}
        {isLive && (
          <div
            style={{
              position: 'absolute',
              top: '16px',
              left: '16px',
              background: '#ef4444',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'white',
                animation: 'pulse 1s infinite',
              }}
            />
            LIVE
          </div>
        )}

        {/* Audio Level Indicator */}
        <div
          style={{
            position: 'absolute',
            bottom: '16px',
            left: '16px',
            right: '16px',
            height: '4px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          <div
            ref={audioLevelBarRef}
            style={{
              width: '0%',
              height: '100%',
              background: '#22c55e',
              transition: 'width 0.1s ease',
            }}
          />
        </div>
      </div>

      {/* Device Selection */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--text-primary)',
          }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '10px',
            }}>
              <i className="fas fa-camera" style={{ color: 'white', fontSize: '12px' }} />
            </div>
            Camera
          </label>
          <div style={{ position: 'relative' }}>
            <select
              value={selectedVideo}
              onChange={(e) => setSelectedVideo(e.target.value)}
              disabled={isLive}
              style={{
                width: '100%',
                padding: '14px 40px 14px 16px',
                background: 'linear-gradient(135deg, #1e1e2e 0%, #252535 100%)',
                border: '2px solid #3f3f5a',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#ffffff',
                cursor: isLive ? 'not-allowed' : 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                transition: 'all 0.2s ease',
                outline: 'none',
              }}
              onFocus={(e) => {
                if (!isLive) e.currentTarget.style.borderColor = '#6366f1';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#3f3f5a';
              }}
            >
              {videoDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
            <i className="fas fa-chevron-down" style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6366f1',
              fontSize: '12px',
              pointerEvents: 'none',
            }} />
          </div>
        </div>

        <div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--text-primary)',
          }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '10px',
            }}>
              <i className="fas fa-microphone" style={{ color: 'white', fontSize: '12px' }} />
            </div>
            Microphone
          </label>
          <div style={{ position: 'relative' }}>
            <select
              value={selectedAudio}
              onChange={(e) => setSelectedAudio(e.target.value)}
              disabled={isLive}
              style={{
                width: '100%',
                padding: '14px 40px 14px 16px',
                background: 'linear-gradient(135deg, #1e1e2e 0%, #252535 100%)',
                border: '2px solid #3f3f5a',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#ffffff',
                cursor: isLive ? 'not-allowed' : 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                transition: 'all 0.2s ease',
                outline: 'none',
              }}
              onFocus={(e) => {
                if (!isLive) e.currentTarget.style.borderColor = '#22c55e';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#3f3f5a';
              }}
            >
              {audioDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
            <i className="fas fa-chevron-down" style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#22c55e',
              fontSize: '12px',
              pointerEvents: 'none',
            }} />
          </div>
        </div>
      </div>

      {/* Control Buttons */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={toggleMute}
          style={{
            flex: 1,
            padding: '14px 20px',
            background: isMuted
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #1e1e2e 0%, #252535 100%)',
            border: isMuted ? 'none' : '2px solid #3f3f5a',
            borderRadius: '10px',
            cursor: 'pointer',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'all 0.2s ease',
            boxShadow: isMuted ? '0 4px 12px rgba(239, 68, 68, 0.4)' : 'none',
          }}
          onMouseOver={(e) => {
            if (!isMuted) {
              e.currentTarget.style.borderColor = '#ef4444';
              e.currentTarget.style.background = 'linear-gradient(135deg, #2a2a3e 0%, #303045 100%)';
            }
          }}
          onMouseOut={(e) => {
            if (!isMuted) {
              e.currentTarget.style.borderColor = '#3f3f5a';
              e.currentTarget.style.background = 'linear-gradient(135deg, #1e1e2e 0%, #252535 100%)';
            }
          }}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: isMuted ? 'rgba(255,255,255,0.2)' : 'rgba(239, 68, 68, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <i className={isMuted ? 'fas fa-microphone-slash' : 'fas fa-microphone'} style={{
              fontSize: '14px',
              color: isMuted ? 'white' : '#ef4444',
            }} />
          </div>
          {isMuted ? 'Unmute' : 'Mute'}
        </button>

        <button
          onClick={toggleVideo}
          style={{
            flex: 1,
            padding: '14px 20px',
            background: isVideoOff
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #1e1e2e 0%, #252535 100%)',
            border: isVideoOff ? 'none' : '2px solid #3f3f5a',
            borderRadius: '10px',
            cursor: 'pointer',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'all 0.2s ease',
            boxShadow: isVideoOff ? '0 4px 12px rgba(239, 68, 68, 0.4)' : 'none',
          }}
          onMouseOver={(e) => {
            if (!isVideoOff) {
              e.currentTarget.style.borderColor = '#6366f1';
              e.currentTarget.style.background = 'linear-gradient(135deg, #2a2a3e 0%, #303045 100%)';
            }
          }}
          onMouseOut={(e) => {
            if (!isVideoOff) {
              e.currentTarget.style.borderColor = '#3f3f5a';
              e.currentTarget.style.background = 'linear-gradient(135deg, #1e1e2e 0%, #252535 100%)';
            }
          }}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: isVideoOff ? 'rgba(255,255,255,0.2)' : 'rgba(99, 102, 241, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <i className={isVideoOff ? 'fas fa-video-slash' : 'fas fa-video'} style={{
              fontSize: '14px',
              color: isVideoOff ? 'white' : '#6366f1',
            }} />
          </div>
          {isVideoOff ? 'Show Video' : 'Hide Video'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '8px',
            color: '#ef4444',
            fontSize: '14px',
          }}
        >
          <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }} />
          {error}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default StreamPreview;
